import { useCallback, useEffect, useState } from 'react';
import { api, getToken, setToken, type Pipeline, type Session } from './api.js';
import { AdminAuth } from './views/AdminAuth.js';
import { Privacidade } from './views/Privacidade.js';
import { Termos } from './views/Termos.js';
import { AdminPanel } from './views/AdminPanel.js';
import { InboxAutomation } from './views/InboxAutomation.js';
import { Auth } from './views/Auth.js';
import { Landing } from './views/Landing.js';
import { Integrations } from './views/Integrations.js';
import { Performance } from './views/Performance.js';
import { Production } from './views/Production.js';
import { Products } from './views/Products.js';
import { Schedule } from './views/Schedule.js';
import { ThemeToggle } from './views/ThemeToggle.js';

type Tab = 'sugestoes' | 'producao' | 'agenda' | 'respostas' | 'desempenho' | 'integracoes';

/**
 * As três primeiras seções são ETAPAS de um fluxo real — uma oferta atravessa
 * as três nesta ordem. Por isso elas viram um trilho com a contagem de cada
 * etapa, e não um menu: "onde o trabalho está parado" é a pergunta que a barra
 * pode responder sem a pessoa clicar em nada.
 *
 * Desempenho e Integrações não são etapas; ficam ao lado, separadas.
 */
const ETAPAS: Array<{ id: Tab; label: string; conta: keyof Pipeline }> = [
  { id: 'sugestoes', label: 'Ofertas', conta: 'ofertas' },
  { id: 'producao', label: 'Produção', conta: 'producao' },
  { id: 'agenda', label: 'Agenda', conta: 'agenda' },
];

const APOIO: Array<{ id: Tab; label: string }> = [
  { id: 'respostas', label: 'Respostas' },
  { id: 'desempenho', label: 'Desempenho' },
  { id: 'integracoes', label: 'Integrações' },
];

const TABS: Array<{ id: Tab; label: string }> = [...ETAPAS, ...APOIO];

export function App() {
  const [user, setUser] = useState<Session['user'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('sugestoes');
  const [mockMode, setMockMode] = useState(false);
  const [pipeline, setPipeline] = useState<Pipeline | null>(null);
  /**
   * Token do PRÓPRIO admin, guardado enquanto ele está dentro da conta de um
   * cliente. "Voltar" restaura este token — sem novo login. Fica só em memória:
   * se a página recarregar, o admin entra de novo, o que é aceitável para uma
   * sessão de suporte de uma hora.
   */
  const [adminToken, setAdminToken] = useState<string | null>(null);
  /**
   * Tela pública: a landing é a porta; o formulário só aparece quando pedido.
   * Guardar o modo (login/cadastro) evita o clique duplo — quem apertou
   * "Criar conta" não deve cair no login para trocar de aba.
   */
  const [portaAuth, setPortaAuth] = useState<'landing' | 'login' | 'register'>('landing');
  /**
   * Estamos em /admin?
   *
   * O endereço é a única fonte: o servidor devolve o SPA para qualquer caminho,
   * então /admin chega aqui como uma navegação normal. Guardar em estado (e não
   * ler o pathname a cada render) deixa o "voltar para o site" trocar a porta
   * sem recarregar a página.
   */
  const [emAdmin, setEmAdmin] = useState(() =>
    window.location.pathname.replace(/\/+$/, '') === '/admin',
  );
  /**
   * Página pública de política de privacidade, em /privacidade.
   *
   * Precisa ser alcançável SEM login: é URL que a Meta exige para publicar o
   * app e que o revisor abre numa janela anônima. Atrás do login, ela não
   * cumpriria a função.
   */
  const [emPrivacidade, setEmPrivacidade] = useState(() =>
    window.location.pathname.replace(/\/+$/, '') === '/privacidade',
  );
  /** Termos de Serviço, também público e também exigido pela Meta. */
  const [emTermos, setEmTermos] = useState(() =>
    window.location.pathname.replace(/\/+$/, '') === '/termos',
  );

  /** Troca a porta e o endereço juntos, para o F5 cair no mesmo lugar. */
  const irPara = useCallback((destino: 'site' | 'admin') => {
    const caminho = destino === 'admin' ? '/admin' : '/';
    window.history.pushState({}, '', caminho);
    setEmAdmin(destino === 'admin');
  }, []);

  // Revalida o token guardado: um token expirado deve levar ao login, não a
  // uma tela quebrada.
  useEffect(() => {
    void (async () => {
      const health = await api.health().catch(() => null);
      if (health) setMockMode(health.mockMode);

      if (!getToken()) {
        setLoading(false);
        return;
      }
      const me = await api.me().catch(() => null);
      setUser(me);
      setLoading(false);
    })();
  }, []);

  const onAuthenticated = useCallback((session: Session) => {
    setToken(session.token);
    setUser(session.user);
  }, []);

  /**
   * Recarrega as contagens do trilho.
   *
   * Roda na troca de aba (o que a pessoa acabou de fazer costuma mudar um
   * número) e a cada 30s, porque o worker publica sozinho e a fila diminui sem
   * ninguém tocar na tela.
   */
  useEffect(() => {
    if (!user) return;
    const carregar = () => {
      void api
        .pipeline()
        .then(setPipeline)
        // Falha aqui não pode derrubar a barra: sem número, o trilho vira só
        // navegação.
        .catch(() => undefined);
    };
    carregar();
    const timer = setInterval(carregar, 30_000);
    return () => clearInterval(timer);
  }, [user, tab]);

  // O título da janela diz onde a pessoa está: com várias abas abertas, cinco
  // guias chamadas "Automação Shopee" são indistinguíveis.
  useEffect(() => {
    const atual = TABS.find((t) => t.id === tab)?.label;
    document.title = atual ? `${atual} · Automação Shopee` : 'Automação Shopee';
  }, [tab]);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setAdminToken(null);
    // Sair devolve à landing, não ao formulário: o formulário era o caminho
    // de entrada, não um lugar para ficar.
    setPortaAuth('landing');
  }, []);

  /** Admin entra na conta de um cliente: troca o token e recarrega a sessão. */
  const entrarNaConta = useCallback(async (token: string) => {
    setAdminToken(getToken());
    setToken(token);
    const me = await api.me().catch(() => null);
    setUser(me);
    setTab('sugestoes');
  }, []);

  /** Volta para a sessão do próprio admin. */
  const voltarParaAdmin = useCallback(async () => {
    if (!adminToken) return logout();
    setToken(adminToken);
    setAdminToken(null);
    const me = await api.me().catch(() => null);
    setUser(me);
  }, [adminToken, logout]);

  if (emTermos) {
    return (
      <Termos
        onVoltar={() => {
          window.history.pushState({}, '', '/');
          setEmTermos(false);
        }}
      />
    );
  }

  if (emPrivacidade) {
    return (
      <Privacidade
        onVoltar={() => {
          window.history.pushState({}, '', '/');
          setEmPrivacidade(false);
        }}
      />
    );
  }

  if (loading) {
    return (
      <div className="auth-wrap">
        <span className="muted">
          <span className="spinner" /> Carregando…
        </span>
      </div>
    );
  }

  if (!user) {
    // /admin nunca cai na landing nem no formulário de cliente.
    if (emAdmin) {
      return <AdminAuth onAuthenticated={onAuthenticated} onSair={() => irPara('site')} />;
    }
    if (portaAuth === 'landing') {
      return (
        <Landing
          onLogin={() => setPortaAuth('login')}
          onRegister={() => setPortaAuth('register')}
        />
      );
    }
    return (
      <Auth
        onAuthenticated={onAuthenticated}
        initialMode={portaAuth}
        onBack={() => setPortaAuth('landing')}
      />
    );
  }

  // Admin da plataforma fora de qualquer conta de cliente: só o painel de
  // contas. As abas de negócio não fazem sentido na conta-âncora dele.
  if (user.role === 'SUPERADMIN' && !user.impersonated) {
    // Token de admin guardado de antes da separação das portas pode chegar aqui
    // com a barra de endereço na raiz. Corrige o endereço sem recarregar, para
    // o F5 e o link copiado levarem ao painel, não à landing.
    if (!emAdmin) {
      window.history.replaceState({}, '', '/admin');
      setEmAdmin(true);
    }
    return (
      <div className="app">
        <header className="topbar">
          <div className="brand">
            <img src="/logo-192.png" alt="" className="brand-mark" width={24} height={24} />
            Automação Shopee
            <span className="badge accent">admin</span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginLeft: 'auto',
              flex: '0 0 auto',
            }}
          >
            <ThemeToggle />
            <button type="button" className="tab" onClick={logout}>
              Sair
            </button>
          </div>
        </header>
        <main id="conteudo">
          <AdminPanel onEnterAccount={(token) => void entrarNaConta(token)} />
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      {/* Com cinco seções na barra, quem navega por teclado passaria por todas
          antes de alcançar a página. */}
      <a className="skip-link" href="#conteudo">
        Ir para o conteúdo
      </a>
      {user.impersonated && (
        <div className="impersonation-bar">
          <span>
            Você está na conta de <strong>{user.account.name}</strong> como administrador.
            Tudo o que fizer aqui vale de verdade.
          </span>
          <button type="button" onClick={() => void voltarParaAdmin()}>
            ← Voltar ao painel de contas
          </button>
        </div>
      )}
      <header className="topbar">
        <div className="brand">
          <img src="/logo-192.png" alt="" className="brand-mark" width={24} height={24} />
          Automação Shopee
        </div>
        {mockMode && (
          <span className="badge warn" title="Nenhuma chamada externa real é feita neste modo">
            modo mock
          </span>
        )}

        <nav className="tabs" aria-label="Seções do painel">
          <div className="rail">
            {ETAPAS.map((e, i) => {
              const n = pipeline?.[e.conta];
              return (
                <span className="rail-slot" key={e.id}>
                  {/* A seta encadeia as etapas: mostra que o trabalho ANDA
                      daqui para lá, em vez de serem três destinos soltos. */}
                  {i > 0 && (
                    <span className="rail-arrow" aria-hidden="true">
                      →
                    </span>
                  )}
                  <button
                    type="button"
                    className="tab"
                    aria-current={tab === e.id ? 'page' : undefined}
                    onClick={() => setTab(e.id)}
                  >
                    {e.label}
                    {/* Zero fica discreto: um "0" em destaque ensina a
                        ignorar o número. */}
                    {n !== undefined && (
                      <span className={`rail-count${n === 0 ? ' is-zero' : ''}`}>{n}</span>
                    )}
                    {/* Falha de publicação não tem etapa própria: aparece na
                        Agenda, que é onde se resolve. Sem isto a pessoa só
                        descobre abrindo a aba. */}
                    {e.id === 'agenda' && (pipeline?.problemas ?? 0) > 0 && (
                      <span
                        className="rail-count is-problem"
                        title={`${pipeline?.problemas} publicação(ões) com falha`}
                      >
                        {pipeline?.problemas}
                      </span>
                    )}
                  </button>
                </span>
              );
            })}
          </div>

          <span className="rail-divider" aria-hidden="true" />

          {APOIO.map((t) => (
            <button
              key={t.id}
              type="button"
              className="tab"
              aria-current={tab === t.id ? 'page' : undefined}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </nav>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: '0 0 auto' }}>
          <ThemeToggle />
          <button type="button" className="tab" onClick={logout} title={user.email}>
            Sair
          </button>
        </div>
      </header>

      <main id="conteudo">
        {tab === 'sugestoes' && <Products onGoToProduction={() => setTab('producao')} />}
        {tab === 'producao' && <Production onScheduled={() => setTab('agenda')} />}
        {tab === 'agenda' && <Schedule />}
        {tab === 'respostas' && <InboxAutomation />}
        {tab === 'desempenho' && <Performance />}
        {tab === 'integracoes' && <Integrations mockMode={mockMode} />}
      </main>
    </div>
  );
}
